import type { Constructor, LitMixinBase } from './mixin-types';
import { property } from 'lit/decorators.js';
import { IUserProfile, UserProfile } from '../datatypes/user-profile';

import { UserProfileContext } from '../contexts/user-profile-context';
import { posDb } from '../database/database';

import { ContextProvider } from '@lit/context';
import { ApiFactory } from '../api/easypos-api';
import { resolveRouterPath, router } from '../router';

export const WithUserProfile = <T extends LitMixinBase>(superClass: T) => {
  class WithUserProfileClass extends superClass {
    private _provider = new ContextProvider(this, {
      context: UserProfileContext,
    });

    @property({ attribute: false })
    private _userProfile?: UserProfile | undefined;
    private _refreshTimer?: ReturnType<typeof setTimeout>;
    public get userProfile(): UserProfile | undefined {
      return this._userProfile;
    }
    public set userProfile(value: UserProfile | undefined) {
      const wasLoggedIn = this._userProfile?.isLoggedIn ?? false;
      this._userProfile = value;
      ApiFactory.instance.setAuthToken(
        value?.accessToken?.encodedLoginAccessToken ?? ''
      );

      this._provider.setValue(value);

      // Routing concerns live above this setter — fresh login navigates
      // from app-login, restore-on-refresh defers to app-index's
      // _routeForShopState, and token renewal must not yank the user away
      // from whatever they're doing. The only auto-redirect we keep is the
      // logout case: an actively logged-in user being cleared lands on the
      // login page. The wasLoggedIn guard avoids redirecting on the
      // unset → unset path (initial load with no cached profile).
      if (!value && wasLoggedIn) {
        void router.navigate(resolveRouterPath('login'));
      }
    }

    async setUserProfile(profile: UserProfile | undefined) {
      this.userProfile = profile;
      if (profile) {
        await posDb.master.setRecord('user-profile', profile.profile);
      } else {
        await posDb.master.deleteRecord('user-profile');
        this._refreshTimer && clearTimeout(this._refreshTimer);
        this._refreshTimer = undefined;
      }
      this.requestUpdate();
    }

    async loadAndVerifyUserProfile() {
      const record = await posDb.master.getRecord<IUserProfile>('user-profile');
      if (record) {
        const userProfile = new UserProfile(record.value);
        if (userProfile.isLoggedIn) this.userProfile = userProfile;
        else this.userProfile = undefined;
      }

      if (this.userProfile) {
        await this.refrehUserToken();
      }
      this.requestUpdate();
    }

    async refrehUserToken() {
      if (!this.userProfile) return;
      if (!this.userProfile.needsRefresh()) {
        const expiresIn = this.userProfile.expiresAtMilliseconds();
        const refreshTime = expiresIn - 3600000; // 1 hour before expiry
        if (refreshTime > 0) {
          this._refreshTimer = setTimeout(
            () => void this.renewUserToken(),
            refreshTime
          );
        }
      } else {
        await this.renewUserToken();
      }
    }
    async renewUserToken() {
      if (!this.userProfile) return;
      const api = ApiFactory.authenticated();
      const response = await api.renew();
      if (response.success) {
        const userProfile = new UserProfile({
          ...this.userProfile.profile,
          accessToken: response.data,
        });
        await this.setUserProfile(userProfile);
      } else await this.clearUserProfile();
    }

    async clearUserProfile() {
      await this.userProfile?.performLogoutOperations();
      this.userProfile = undefined;
      await posDb.master.deleteRecord('user-profile');
      ApiFactory.instance.setAuthToken('');
      this.requestUpdate();
    }

    // Register lock: de-authorise the current identity WITHOUT the logout
    // navigation. Unlike clearUserProfile (which routes to /login via the
    // setter's wasLoggedIn guard), this clears the token + persisted profile
    // and drops the in-memory profile directly, leaving the caller to render
    // the lock screen in place. Bypasses the public setter on purpose.
    async clearAuthForLock() {
      await this.userProfile?.performLogoutOperations();
      this._userProfile = undefined;
      this._provider.setValue(undefined);
      ApiFactory.instance.setAuthToken('');
      await posDb.master.deleteRecord('user-profile');
      this._refreshTimer && clearTimeout(this._refreshTimer);
      this._refreshTimer = undefined;
      this.requestUpdate();
    }
  }

  return WithUserProfileClass as Constructor<WithUserProfileInterface> & T;
};

export interface WithUserProfileInterface {
  userProfile?: UserProfile;
  loadAndVerifyUserProfile(): Promise<void>;
  setUserProfile(profile: UserProfile | undefined): Promise<void>;
  clearUserProfile(): Promise<void>;
  clearAuthForLock(): Promise<void>;
}
